import { getGroupingModel } from '../lib/grouping';
import { drillLocationsEqual } from '../lib/drill/drillLocation';
import type {
  DrillLocation,
  ExplorationFilter,
  ExplorationSelection,
  ExplorationState,
} from '../types/exploration';

/**
 * Pure navigation bookkeeping behind the exploration reducer: where a selection lives, when a
 * visit gets pushed onto the drill history, and how that history reconciles with the URL.
 *
 * Split out of `ExplorationContext.tsx` so these can be unit-tested directly — they encode the
 * browser Back/Forward behaviour that is otherwise only covered transitively through the
 * reducer, and is the most subtle logic in the module.
 */

export function locationForSelection(
  state: Pick<ExplorationState, 'secondary' | 'tagKey' | 'groupByAccount' | 'view' | 'drillPath'>,
  selection: NonNullable<ExplorationSelection>
): DrillLocation | null {
  const model = getGroupingModel(state.secondary, state.tagKey);
  const opts = {
    tagKey: state.tagKey,
    groupByAccount: state.groupByAccount,
    view: state.view,
    currentLocation: state.drillPath,
  };
  if (selection.kind === 'volume') {
    return model.locationForVolume(selection.volume, opts);
  }
  const anchor = selection.vols.find((v) => v.instanceId === selection.instanceId);
  return anchor ? model.locationForInstance(anchor, selection.instanceId, opts) : null;
}

/**
 * When selection deepens/changes the canonical location (e.g. Supported → instance),
 * push the prior path onto the visit stack so canvas out / Back restore it.
 *
 * Volume selection at an unchanged location still pushes so Back/out goes
 * volume → volume → group (URL gains/loses/changes the volume param) instead
 * of leaving the group. Every volume selection — including switching from
 * one volume to another within the same group — is its own real browser
 * history entry (urlHistoryWrite: 'push'), so native Back steps through them
 * one at a time via syncFromUrl; it is not replayed from the in-memory
 * drillHistory stack, which only gates whether canvas out attempts
 * navigate(-1) at all (see BubbleCanvas.tsx's handleDrillUp).
 */
export function selectionNavUpdate(
  state: ExplorationState,
  nextLocation: DrillLocation | null,
  opts?: { pushWhenLocationUnchanged?: boolean }
): Pick<
  ExplorationState,
  'drillPath' | 'drillHistory' | 'urlHistoryWrite' | 'overflowPages'
> {
  if (nextLocation == null) {
    return {
      drillPath: state.drillPath,
      drillHistory: state.drillHistory,
      urlHistoryWrite: 'replace',
      overflowPages: state.overflowPages,
    };
  }
  if (drillLocationsEqual(nextLocation, state.drillPath)) {
    if (!opts?.pushWhenLocationUnchanged) {
      return {
        drillPath: state.drillPath,
        drillHistory: state.drillHistory,
        urlHistoryWrite: 'replace',
        overflowPages: state.overflowPages,
      };
    }
    return {
      drillPath: state.drillPath,
      drillHistory: [...state.drillHistory, state.drillPath],
      urlHistoryWrite: 'push',
      overflowPages: state.overflowPages,
    };
  }
  return {
    drillPath: nextLocation,
    drillHistory: [...state.drillHistory, state.drillPath],
    urlHistoryWrite: 'push',
    // Re-rooting the canvas invalidates every `+ N More` key (they are built
    // relative to the current drill root by hierarchyCapKey), so a kept page
    // would silently expand whichever unrelated group now owns that key.
    overflowPages: {},
  };
}

/** Drop a same-location volume visit frame when selection is cleared without Back. */
export function drillHistoryAfterClearingVolumeSelection(
  state: ExplorationState
): DrillLocation[] {
  if (state.selectedNode?.kind !== 'volume' || state.drillHistory.length === 0) {
    return state.drillHistory;
  }
  const top = state.drillHistory[state.drillHistory.length - 1]!;
  if (!drillLocationsEqual(top, state.drillPath)) return state.drillHistory;
  return state.drillHistory.slice(0, -1);
}

export function filtersEqual(
  a: ExplorationFilter[],
  b: ExplorationFilter[]
): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** Push current location onto the visit stack only when filters become/stay non-empty. */
export function filterNavHistory(
  state: ExplorationState,
  nextFilters: ExplorationFilter[]
): DrillLocation[] {
  if (nextFilters.length === 0) return state.drillHistory;
  return [...state.drillHistory, state.drillPath];
}

/** Align visit stack with a URL-applied location (browser Back/Forward). */
export function drillHistoryAfterUrlSync(
  history: DrillLocation[],
  currentPath: DrillLocation,
  snapshotPath: DrillLocation
): DrillLocation[] {
  for (let i = history.length - 1; i >= 0; i--) {
    if (drillLocationsEqual(history[i]!, snapshotPath)) {
      return history.slice(0, i);
    }
  }
  if (drillLocationsEqual(currentPath, snapshotPath)) {
    return history;
  }
  return [];
}

